"use client";

import Image from "next/image";
import { motion } from "framer-motion";
import {
  SiExpress,
  SiFlutter,
  SiNextdotjs,
  SiNodedotjs,
  SiReact,
  SiTailwindcss,
  SiTypescript,
} from "react-icons/si";

import { cn } from "@/lib/utils";

const techStack = [
  { name: "React", icon: <SiReact /> },
  { name: "Next.js", icon: <SiNextdotjs /> },
  { name: "Node.js", icon: <SiNodedotjs /> },
  { name: "Tailwind", icon: <SiTailwindcss /> },
  { name: "TypeScript", icon: <SiTypescript /> },
  { name: "Express", icon: <SiExpress /> },
  { name: "React Native", icon: <SiReact /> },
  { name: "Flutter", icon: <SiFlutter /> },
];

export const BentoGrid = ({
  className,
  children,
}: {
  className?: string;
  children?: React.ReactNode;
}) => {
  return (
    <div
      className={cn(
        "grid grid-cols-1 lg:grid-cols-12 gap-6 mx-auto auto-rows-[minmax(170px,auto)]",
        className,
      )}
    >
      {children}
    </div>
  );
};

export const BentoGridItem = ({
  className,
  id,
  title,
  subtitle,
  description,
  img,
  imgClassName,
}: {
  className?: string;
  id: number;
  title?: string | React.ReactNode;
  subtitle?: string;
  description?: string | React.ReactNode;
  img?: string;
  imgClassName?: string;
}) => {
  const isProfileCard = id === 1;
  const isTechCard = id === 3;
  const isCtaCard = id === 6;

  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.55, ease: "easeOut" }}
      className={cn(
        "relative overflow-hidden rounded-[20px] border border-white/10 p-6",
        "bg-[linear-gradient(120deg,rgba(8,11,28,0.95),rgba(15,21,48,0.95))]",
        "transition-transform duration-300 ease-out hover:-translate-y-1.5",
        className,
      )}
    >
      <div
        className={cn("relative z-10 h-full flex flex-col", "justify-between")}
      >
        {isProfileCard && (
          <div className="h-full grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
            <div className="relative min-h-[260px] rounded-[16px] overflow-hidden border border-white/10">
              {img && (
                <>
                  <Image
                    src={img}
                    alt={typeof title === "string" ? title : "Profile"}
                    fill
                    className={cn(
                      "object-cover object-[center_16%] scale-[1.08] brightness-[0.88]",
                      imgClassName,
                    )}
                  />
                  <div
                    className="absolute inset-0"
                    style={{
                      background:
                        "linear-gradient(to top, rgba(0,0,0,0.8), rgba(0,0,0,0.2))",
                    }}
                  />
                </>
              )}
            </div>

            <div className="flex flex-col justify-center">
              <p className="text-white-200 text-sm uppercase tracking-[0.2em]">
                About Me
              </p>
              <h3 className="text-white font-bold text-lg sm:text-2xl md:text-4xl leading-tight mt-2">
                {title}
              </h3>
              {subtitle && (
                <p className="text-purple mt-2 text-base sm:text-lg md:text-xl font-semibold leading-tight">
                  {subtitle}
                </p>
              )}
              {description && (
                <p className="text-white-100 mt-3 sm:mt-4 text-sm sm:text-base leading-relaxed max-w-[38ch]">
                  {description}
                </p>
              )}
            </div>
          </div>
        )}

        {!isCtaCard && (
          <div>
            {!isProfileCard && (
              <>
                <h3 className="text-white font-bold text-[22px] leading-tight">
                  {title}
                </h3>
                {description && (
                  <p className="text-white-100 mt-3 text-[15px] leading-relaxed">
                    {description}
                  </p>
                )}
              </>
            )}
          </div>
        )}

        {isTechCard && (
          <div className="mt-5 grid grid-cols-2 md:grid-cols-3 gap-3">
            {techStack.map((tech) => (
              <span
                key={tech.name}
                className="text-[14px] text-white rounded-full border border-white/15 bg-black/30 px-3 py-2 transition-all duration-200 ease-out hover:-translate-y-[3px] hover:border-purple/70 hover:shadow-[0_0_12px_rgba(99,102,241,0.4)]"
              >
                <span className="flex items-center justify-center gap-2">
                  <span className="text-[15px] text-white/90">{tech.icon}</span>
                  <span>{tech.name}</span>
                </span>
              </span>
            ))}
          </div>
        )}

        {isCtaCard && (
          <div
            className="h-full rounded-[14px] p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
            style={{ background: "linear-gradient(135deg,#6366f1,#9333ea)" }}
          >
            <div>
              <h3 className="text-white font-bold text-[30px] leading-tight">
                {title}
              </h3>
              <p className="text-white/90 text-[16px] mt-2 leading-relaxed">
                {description}
              </p>
            </div>
            <a
              href="#contact"
              className="inline-flex items-center justify-center text-white border border-white/35 rounded-full px-5 py-2.5 text-base font-semibold transition hover:bg-white/15"
            >
              Start a Project →
            </a>
          </div>
        )}
      </div>
    </motion.article>
  );
};
